import { useTheme } from "../../../hooks/useTheme";
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { isDesktop } from "react-device-detect";
import ProfilePic from "../../ProfilePic";
import {
  AccountData,
  MembershipPlan,
  MembershipPromoCode,
  MembershipPromoCodeSource,
  MembershipPromoCodeStatus,
  NotificationType,
  ThemeType,
} from "@markit/common.types";
import { convertToMoney, isValidEmail } from "@markit/common.utils";
import RectangleButton from "../../Buttons/RectangleButton";
import { checkValidMembershipPromoCode } from "../../../utils/membershipUtils";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import AlertContainer from "../../Containers/AlertContainer";
import { API } from "../../../API";
import { MixpanelContext } from "../../../context/AnalyticsService";
import { Elements } from "@stripe/react-stripe-js";
import SubscriptionCheckoutForm, {
  SubscriptionCheckoutType,
} from "../../Subscription/SubscriptionCheckoutForm";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import { CircularProgress } from "@mui/material";
import CustomTextField from "../../CustomTextField";
import { getUserFollowerRef } from "../../../utils/FirebaseUtils";
import { getDoc, onSnapshot } from "../../../firebase";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { useDispatch, useSelector } from "react-redux";
import {
  onNavigateRedirectPath,
  onProfilePageNavigatePath,
} from "../../../utils/navigationUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { getAccountState } from "../../../redux/slices/accountSlice";
import {
  getStripePromise,
  testStripe,
} from "../../../utils/stripeAccountUtils";

type SubscribeFlowMembershipCheckoutProps = {
  creatorData: AccountData;
  selectedMembershipPlan: MembershipPlan;
  uid: string;
  backOnPress: () => void;
  onSaveFormResponses?: () => Promise<void>; // Membership page flow only
  themeType?: ThemeType;
};

const SubscribeFlowMembershipCheckout = (
  props: SubscribeFlowMembershipCheckoutProps
) => {
  const {
    creatorData,
    selectedMembershipPlan,
    uid,
    backOnPress,
    onSaveFormResponses,
    themeType,
  } = props;
  const { theme } = useTheme(themeType);
  const { redirectPath } = useSelector(getAccountState).account;
  const pathname = useLocation().pathname;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const [showingPaymentSheet, setShowingPaymentSheet] =
    useState<boolean>(false);
  const [options, setOptions] = useState<{
    clientSecret: string;
    appearance: {};
  }>();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });
  const [codeInput, setCodeInput] = useState<string>("");
  const [applyCodeError, setApplyCodeError] = useState("");
  const [discountCode, setDiscountCode] = useState<
    MembershipPromoCode | undefined
  >(undefined);
  const [groupPlanCodeOwner, setGroupPlanCodeOwner] = useState<AccountData>();
  const [loading, setLoading] = useState(false);

  const styles = {
    errorText: { fontSize: 12, color: Colors.RED3 },
  };

  const isManageSubscription = useMemo(
    () => pathname.includes("manageSubscription"),
    [pathname]
  );

  const isFreePromo = useMemo(
    () => discountCode && discountCode.value === 100,
    [discountCode]
  );

  const isFreeLifetimePromo = useMemo(
    () => isFreePromo && discountCode?.durationInBillingCycles === 0,
    [discountCode, isFreePromo]
  );

  // Final price with discount applied or original selected price (in cents)
  const finalPrice = useMemo(
    () =>
      discountCode
        ? selectedMembershipPlan.price * (1 - discountCode.value / 100)
        : selectedMembershipPlan.price,
    [discountCode, selectedMembershipPlan.price]
  );

  const onPurchaseSuccess = useCallback(async () => {
    const handlePurchase = async () => {
      // Store the form answers to the follower (only for membership page flow)
      if (onSaveFormResponses) {
        await onSaveFormResponses();
      }

      showNotificationBanner(
        dispatch,
        isFreePromo ? "Redeemed" : "Purchased",
        NotificationType.AFFIRMATIVE
      );
      if (redirectPath) {
        onNavigateRedirectPath(navigate, dispatch, redirectPath);
      } else {
        onProfilePageNavigatePath(navigate, creatorData.username);
      }
      setLoading(false);
    };

    // Add a snapshot to listen for when the follower membership plan matches the selected plan
    // Once it's created, we know that they have gotten their paid subscription
    const followerRef = getUserFollowerRef(creatorData.uid, uid);

    // Check immediately first
    const immediateSnapshot = await getDoc(followerRef);
    if (
      immediateSnapshot.exists() &&
      immediateSnapshot.data().membershipPlanId === selectedMembershipPlan.id
    ) {
      handlePurchase();
      return;
    }

    const unsubscribe = onSnapshot(followerRef, async (snapshot) => {
      const followerData = snapshot.data();
      if (
        followerData &&
        followerData.membershipPlanId === selectedMembershipPlan.id
      ) {
        handlePurchase();
        unsubscribe();
      }
    });
  }, [
    onSaveFormResponses,
    creatorData.uid,
    creatorData.username,
    dispatch,
    isFreePromo,
    navigate,
    redirectPath,
    selectedMembershipPlan.id,
    uid,
  ]);

  const fetchPaymentSheetParams = useCallback(async () => {
    setOptions(undefined);
    setLoading(true);
    await API.user
      .initializeFollowerSubscription({
        uid: uid,
        email: email,
        priceId: selectedMembershipPlan.id,
        creatorStripeAccountId:
          testStripe(creatorData.uid) && creatorData.testStripeAccountId
            ? creatorData.testStripeAccountId
            : creatorData.stripeAccountId,
        creatorUserId: creatorData.uid,
        membershipPromoCodeId: discountCode?.id ?? "",
      })
      .then(async (res: any) => {
        if (isFreeLifetimePromo) {
          onPurchaseSuccess();
          return;
        }
        const { clientSecret } = res;
        const stripeOptions = {
          clientSecret: clientSecret,
          appearance: {
            theme: theme.StripeTheme.theme,
            variables: {
              colorPrimary: Colors.BLUE5,
              colorBackground: theme.SecondaryBG.backgroundColor,
            },
          },
        };
        setOptions(stripeOptions);
        setShowingPaymentSheet(true);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error("Error on initialize: " + err.message);
        setLoading(false);
      });
  }, [
    creatorData.stripeAccountId,
    creatorData.testStripeAccountId,
    creatorData.uid,
    discountCode?.id,
    email,
    isFreeLifetimePromo,
    onPurchaseSuccess,
    selectedMembershipPlan.id,
    theme.SecondaryBG.backgroundColor,
    theme.StripeTheme.theme,
    uid,
  ]);

  const verifyCodeOnPress = useCallback(async () => {
    if (codeInput.length === 0) {
      setApplyCodeError("You must enter a code to apply.");
      return;
    }

    const upperCaseCodeInput = codeInput.toUpperCase().trim();

    // check if discount code is valid
    const { status, promoCode, groupPlanOwner } =
      await checkValidMembershipPromoCode(
        creatorData.uid,
        upperCaseCodeInput,
        selectedMembershipPlan.id
      );

    if (status === MembershipPromoCodeStatus.PROMO_CODE_VALID) {
      setDiscountCode(promoCode);
      setGroupPlanCodeOwner(groupPlanOwner);
      setApplyCodeError("");
      // close the payment sheet if it's currently visible
      if (showingPaymentSheet) {
        setShowingPaymentSheet(false);
        setOptions(undefined);
      }

      mixpanel.track("Webapp: Entered Code For Membership Discount Code", {
        subscription_plan: selectedMembershipPlan.name,
        subscription_plan_amount: selectedMembershipPlan.price,
        code_input: codeInput,
      });
    } else {
      setApplyCodeError(status);
    }
  }, [
    codeInput,
    creatorData.uid,
    mixpanel,
    selectedMembershipPlan.id,
    selectedMembershipPlan.name,
    selectedMembershipPlan.price,
    showingPaymentSheet,
  ]);

  const showPaymentSheetOrRedeem = useCallback(async () => {
    let showError = false;
    if (email === "" || !isValidEmail(email)) {
      setEmailError(true);
      showError = true;
    }
    if (showError) {
      return;
    }
    fetchPaymentSheetParams();
  }, [email, fetchPaymentSheetParams]);

  const onConfirmApplePay = useCallback(() => {
    if (mixpanel) {
      mixpanel.track("Webapp: Getting Follower Subscription With Apple Pay", {
        distinct_id: uid,
        creator_uid: creatorData.uid,
        creator_stripe_account_id: creatorData.stripeAccountId,
        membership_plan_id: selectedMembershipPlan.id,
        is_manage_subscription: isManageSubscription,
      });
    }
    onPurchaseSuccess();
  }, [
    creatorData.stripeAccountId,
    creatorData.uid,
    isManageSubscription,
    mixpanel,
    onPurchaseSuccess,
    selectedMembershipPlan.id,
    uid,
  ]);

  const paymentElement = useMemo(() => {
    if (options !== undefined) {
      return (
        <div className="SubscriptionCheckoutPaymentSheet">
          <Elements
            key={options.clientSecret}
            stripe={getStripePromise(creatorData.uid)}
            options={options}
          >
            <SubscriptionCheckoutForm
              label={selectedMembershipPlan.name}
              amount={finalPrice}
              processing={loading}
              setProcessing={setLoading}
              isReadyForPayment={true}
              clientSecret={options.clientSecret}
              onConfirmApplePay={onConfirmApplePay}
              themeType={themeType}
              checkoutType={SubscriptionCheckoutType.FOLLOWER_MEMBERSHIP}
              onPress={async (stripe: Stripe, elements: StripeElements) => {
                if (mixpanel) {
                  mixpanel.track(
                    "Webapp: Attempting Getting Follower Subscription",
                    {
                      distinct_id: uid,
                      creator_uid: creatorData.uid,
                      creator_stripe_account_id: creatorData.stripeAccountId,
                      membership_plan_id: selectedMembershipPlan.id,
                      is_manage_subscription: isManageSubscription,
                    }
                  );
                }
                setLoading(true);
                const { error } = isFreePromo
                  ? await stripe.confirmSetup({
                      elements,
                      redirect: "if_required",
                    })
                  : await stripe.confirmPayment({
                      elements,
                      redirect: "if_required",
                    });

                if (error) {
                  // This point will only be reached if there is an immediate error when
                  // confirming the payment. Show error to your customer (for example, payment
                  // details incomplete)
                  console.error(error.message);
                  setAlertText({
                    heading:
                      error.message ??
                      "An error occurred when entering your info.",
                    subHeading: "Please try again.",
                  });
                  mixpanel.track(
                    "Webapp: Error Getting Follower Subscription",
                    {
                      distinct_id: uid,
                      creator_uid: creatorData.uid,
                      creator_stripe_account_id: creatorData.stripeAccountId,
                      membership_plan_id: selectedMembershipPlan.id,
                      error_message: error.message,
                    }
                  );
                  setLoading(false);
                } else {
                  onPurchaseSuccess();
                }
              }}
            />
          </Elements>
        </div>
      );
    } else if (showingPaymentSheet) {
      return (
        <div className="ColumnCentering">
          <CircularProgress style={{ color: Colors.GRAY1 }} size={24} />
        </div>
      );
    } else {
      return undefined;
    }
  }, [
    options,
    showingPaymentSheet,
    selectedMembershipPlan.name,
    selectedMembershipPlan.id,
    finalPrice,
    loading,
    onConfirmApplePay,
    themeType,
    mixpanel,
    isFreePromo,
    uid,
    creatorData.uid,
    creatorData.stripeAccountId,
    isManageSubscription,
    onPurchaseSuccess,
  ]);

  return (
    <div
      className="ColumnNormal"
      style={{
        marginInline: "auto",
        width: isDesktop ? 544 : "100%",
        gap: 30,
      }}
    >
      <div
        className="ColumnCentering"
        style={{ gap: isDesktop ? 30 : 14, paddingInline: isDesktop ? 0 : 14 }}
      >
        <ProfilePic size={isDesktop ? 100 : 75} user={creatorData} />
        <div className="ColumnCentering" style={{ gap: 7 }}>
          <span className="sectionTitle" style={{ ...theme.PrimaryText }}>
            Payment
          </span>
          <div
            onClick={backOnPress}
            className="AlignedRowSelect"
            style={{ gap: 2 }}
          >
            <Icon icon={"ion:chevron-left"} height={11} style={theme.SubText} />
            <span className="smallBodySubtext">Back to plans</span>
          </div>
        </div>
      </div>
      <div
        className="ColumnNormal"
        style={{
          width: isDesktop ? 330 : "100%",
          alignSelf: "center",
          gap: 14,
        }}
      >
        <div
          className="ColumnNormal"
          style={{ gap: 7, paddingInline: isDesktop ? 0 : 14 }}
        >
          <span className="bodyMedium" style={theme.PrimaryText}>
            Enter email{" "}
            <span className="smallBodySubtext">
              (required to receive receipt)
            </span>
          </span>
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <CustomTextField
              value={email}
              inputMode="email"
              placeholder="Enter Email"
              borderRadius={12}
              backgroundColor={theme.SecondaryBG.backgroundColor}
              onChange={(change: any) => {
                setEmailError(false);
                setEmail(change.target.value);
              }}
              error={emailError}
              eventTheme={themeType}
            />
            {emailError ? (
              <span style={styles.errorText}>Email required</span>
            ) : null}
          </div>
        </div>
        <div
          className="ColumnNormal"
          style={{ gap: 4, paddingInline: isDesktop ? 0 : 14 }}
        >
          <div className="AlignedRowSpaced" style={{ gap: 14 }}>
            <CustomTextField
              value={codeInput}
              disabled={!!discountCode}
              inputMode="text"
              placeholder="Enter Code"
              borderRadius={12}
              onChange={(name: any) => {
                setApplyCodeError("");
                setCodeInput(name.target.value);
              }}
              error={applyCodeError !== ""}
              endAdornment={
                discountCode || applyCodeError ? (
                  <Icon
                    icon={
                      discountCode ? "ion:checkmark-circle" : "ion:close-circle"
                    }
                    height={20}
                    style={{
                      marginRight: 10,
                      color: discountCode ? Colors.GREEN2 : Colors.RED3,
                    }}
                  />
                ) : undefined
              }
              eventTheme={themeType}
            />
            <div className="AlignedRow">
              {discountCode ? (
                <span
                  className="bodyMedium"
                  style={{ whiteSpace: "nowrap", ...theme.PrimaryText }}
                >
                  -
                  {convertToMoney(
                    (selectedMembershipPlan.price * discountCode.value) / 10000
                  )}
                </span>
              ) : (
                <RectangleButton
                  buttonLabel={
                    <span className="bodyMedium">{"Apply Promo"}</span>
                  }
                  onPress={verifyCodeOnPress}
                  altColor={theme.ButtonBG.backgroundColor}
                  altTextColor={theme.ButtonText.color}
                  containerStyles={{
                    padding: 10,
                    whiteSpace: "nowrap",
                  }}
                />
              )}
            </div>
          </div>
          {applyCodeError ? (
            <span style={{ ...styles.errorText }}>{applyCodeError}</span>
          ) : null}
          {discountCode ? (
            discountCode.source === MembershipPromoCodeSource.SYSTEM &&
            groupPlanCodeOwner ? (
              <span className="bodySmallRegular" style={theme.PrimaryText}>
                Redeeming {groupPlanCodeOwner.fullName}&apos;s group plan
              </span>
            ) : (
              <span className="bodySmallRegular" style={theme.PrimaryText}>
                Discount applied every month{" "}
                {discountCode.durationInBillingCycles === 0
                  ? "forever"
                  : `for ${discountCode.durationInBillingCycles} months`}
              </span>
            )
          ) : null}
        </div>
        <div
          className="AlignedRowSpaced"
          style={{ paddingInline: isDesktop ? 0 : 14 }}
        >
          <span style={{ fontWeight: 500, ...theme.PrimaryText }}>Total</span>
          <span style={{ fontSize: 24, fontWeight: 500, ...theme.PrimaryText }}>
            {discountCode ? (
              <span>
                <s>{convertToMoney(selectedMembershipPlan.price / 100)}</s>{" "}
              </span>
            ) : null}
            {convertToMoney(finalPrice / 100)}
          </span>
        </div>
        <div style={{ paddingInline: isDesktop ? 0 : 14 }}>
          {paymentElement}
          {!showingPaymentSheet ? (
            <RectangleButton
              buttonLabel={
                isFreeLifetimePromo
                  ? "Redeem"
                  : isFreePromo
                    ? "Continue"
                    : "Continue to Payment"
              }
              onPress={showPaymentSheetOrRedeem}
              altPaddingVert={14}
              altPaddingHorz={14}
              altColor={theme.ButtonBG.backgroundColor}
              altTextColor={theme.ButtonText.color}
              loading={loading}
            />
          ) : null}
        </div>
      </div>
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        closeModal={() => {
          setAlertText({ heading: "", subHeading: "" });
        }}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
    </div>
  );
};

export default memo(SubscribeFlowMembershipCheckout);
